import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import Modal from '../components/Modal';

interface ImportChange {
  id: number;
  entity_type: 'controller' | 'supervisor';
  entity_id: number;
  entity_name: string;
  original_entity_id: number;
  server_id: number;
  change_type: 'new' | 'updated';
  field_changes: Record<string, { old_value: any; new_value: any }>;
  contract_id?: string;
  contract_name?: string;
  is_reviewed: boolean;
  is_applied: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  review_action?: string;
  review_notes?: string;
  import_source: string;
  import_timestamp: string;
  created_at: string;
  updated_at?: string;
}

interface ImportChangeSummary {
  total_unreviewed: number;
  by_entity_type: {
    controllers: number;
    supervisors: number;
  };
  by_change_type: {
    new: number;
    updated: number;
  };
  with_contracts: number;
  without_contracts: number;
}

const ImportChangesDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState<ImportChange[]>([]);
  const [summary, setSummary] = useState<ImportChangeSummary | null>(null);
  const [selectedChange, setSelectedChange] = useState<ImportChange | null>(null);
  const [selectedChanges, setSelectedChanges] = useState<number[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showBulkReviewModal, setShowBulkReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<'apply' | 'reject' | 'partial'>('apply');
  const [bulkReviewAction, setBulkReviewAction] = useState<'apply' | 'reject'>('apply');
  const [reviewNotes, setReviewNotes] = useState('');
  const [bulkReviewNotes, setBulkReviewNotes] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [filter, setFilter] = useState({
    entity_type: '',
    change_type: '',
    has_contract: ''
  });
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  const [cleanupError, setCleanupError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filter.entity_type) params.append('entity_type', filter.entity_type);
      if (filter.change_type) params.append('change_type', filter.change_type);
      if (filter.has_contract) params.append('has_contract', filter.has_contract);
      params.append('is_reviewed', 'false');
      params.append('limit', '50');
      
      // Fetch changes and summary
      const [changesRes, summaryRes] = await Promise.all([
        apiService.get<ImportChange[]>(`/api/v1/import-changes?${params.toString()}`),
        apiService.get<ImportChangeSummary>('/api/v1/import-changes/stats/summary')
      ]);
      
      setChanges(changesRes);
      setSummary(summaryRes);
    } catch (error) {
      console.error('Error fetching import changes:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const triggerCleanup = async () => {
    setCleanupLoading(true);
    setCleanupMessage(null);
    setCleanupError(null);
    try {
      const res = await apiService.post<{
        message?: string;
        deactivated_customers?: number;
        deleted_contracts?: number;
      }>('/api/v1/customers/maintenance/cleanup-supervised-controllers');

      const summaryText = res?.message
        || `Cleanup finished: ${res?.deactivated_customers || 0} customers deactivated, ${res?.deleted_contracts || 0} contracts removed`;

      setCleanupMessage(summaryText);
      fetchData();
    } catch (error: any) {
      const apiError = error?.response?.data?.detail || error?.message || 'Cleanup failed';
      setCleanupError(apiError);
    } finally {
      setCleanupLoading(false);
    }
  };
 
  
  const handleReview = (change: ImportChange) => {
    setSelectedChange(change);
    setReviewAction('apply');
    setReviewNotes('');
    setSelectedFields(Object.keys(change.field_changes));
    setShowReviewModal(true);
  };

  const submitReview = async () => {
    if (!selectedChange) return;
    
    try {
      const payload: any = {
        review_action: reviewAction,
        review_notes: reviewNotes
      };
      
      if (reviewAction === 'partial') {
        payload.fields_to_apply = selectedFields;
      }
      
      await apiService.post(`/api/v1/import-changes/${selectedChange.id}/review`, payload);
      
      setShowReviewModal(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error reviewing change:', error);
    }
  };

  const toggleFieldSelection = (field: string) => {
    setSelectedFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedChanges(changes.map((c: ImportChange) => c.id));
    } else {
      setSelectedChanges([]);
    }
  };

  const handleSelectChange = (changeId: number, checked: boolean) => {
    if (checked) {
      setSelectedChanges(prev => [...prev, changeId]);
    } else {
      setSelectedChanges(prev => prev.filter(id => id !== changeId));
    }
  };

  const handleBulkReview = (action: 'apply' | 'reject') => {
    setBulkReviewAction(action);
    setBulkReviewNotes('');
    setShowBulkReviewModal(true);
  };

  const submitBulkReview = async () => {
    if (selectedChanges.length === 0) return;
    
    try {
      // Process each change individually since there's no bulk endpoint
      const promises = selectedChanges.map(changeId => 
        apiService.post(`/api/v1/import-changes/${changeId}/review`, {
          review_action: bulkReviewAction,
          review_notes: bulkReviewNotes
        })
      );
      
      await Promise.all(promises);
      
      setShowBulkReviewModal(false);
      setSelectedChanges([]);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error bulk reviewing changes:', error);
    }
  };

  const allSelected = changes.length > 0 && selectedChanges.length === changes.length;
  const someSelected = selectedChanges.length > 0 && selectedChanges.length < changes.length;

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Import Changes Dashboard</h1>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-number">{summary.total_unreviewed}</div>
            <div className="stat-label">Unreviewed Changes</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{summary.by_entity_type.controllers}</div>
            <div className="stat-label">Controller Changes</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{summary.by_entity_type.supervisors}</div>
            <div className="stat-label">Supervisor Changes</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{summary.by_change_type.new}</div>
            <div className="stat-label">New Entities</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{summary.by_change_type.updated}</div>
            <div className="stat-label">Updated Entities</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{summary.with_contracts}</div>
            <div className="stat-label">With Contracts</div>
          </div>
        </div>
      )}

      {/* Maintenance */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3 className="card-title">Maintenance</h3>
        </div>
        <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0 }}>
              Clean up controller customers that already belong to a supervisor to prevent duplicate billing lines.
            </p>
            {cleanupMessage && (
              <div className="alert alert-success" style={{ marginTop: '8px' }}>
                {cleanupMessage}
              </div>
            )}
            {cleanupError && (
              <div className="alert alert-danger" style={{ marginTop: '8px' }}>
                {cleanupError}
              </div>
            )}
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={triggerCleanup}
            disabled={cleanupLoading}
          >
            {cleanupLoading ? 'Cleaning…' : 'Clean Up Controller Customers'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3 className="card-title">Filters</h3>
        </div>
        <div style={{ padding: '15px', display: 'flex', gap: '15px' }}>
          <select 
            value={filter.entity_type} 
            onChange={(e) => setFilter({...filter, entity_type: e.target.value})}
            className="form-control"
            style={{ width: '200px' }}
          >
            <option value="">All Entity Types</option>
            <option value="controller">Controllers</option>
            <option value="supervisor">Supervisors</option>
          </select>
          
          <select 
            value={filter.change_type} 
            onChange={(e) => setFilter({...filter, change_type: e.target.value})}
            className="form-control"
            style={{ width: '200px' }}
          >
            <option value="">All Change Types</option>
            <option value="new">New</option>
            <option value="updated">Updated</option>
          </select>
          
          <select 
            value={filter.has_contract} 
            onChange={(e) => setFilter({...filter, has_contract: e.target.value})}
            className="form-control"
            style={{ width: '200px' }}
          >
            <option value="">All</option>
            <option value="true">With Contract</option>
            <option value="false">Without Contract</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Panel */}
      {selectedChanges.length > 0 && (
        <div className="card" style={{ marginBottom: '20px', backgroundColor: '#e3f2fd' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>{selectedChanges.length} change{selectedChanges.length !== 1 ? 's' : ''} selected</strong>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn btn-success btn-sm"
                onClick={() => handleBulkReview('apply')}
              >
                Apply All Selected
              </button>
              <button 
                className="btn btn-danger btn-sm"
                onClick={() => handleBulkReview('reject')}
              >
                Reject All Selected
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedChanges([])}
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Changes Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Unreviewed Changes</h3>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Type</th>
                <th>Entity</th>
                <th>Change Type</th>
                <th>Contract</th>
                <th>Affected Fields</th>
                <th>Import Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {changes.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                    No unreviewed changes found
                  </td>
                </tr>
              ) : (
                changes.map((change) => (
                  <tr key={change.id} style={{ backgroundColor: selectedChanges.includes(change.id) ? '#f5f5f5' : '' }}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedChanges.includes(change.id)}
                        onChange={(e) => handleSelectChange(change.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <span className={`badge ${change.entity_type === 'controller' ? 'badge-info' : 'badge-primary'}`}>
                        {change.entity_type}
                      </span>
                    </td>
                    <td>{change.entity_name}</td>
                    <td>
                      <span className={`badge ${change.change_type === 'new' ? 'badge-success' : 'badge-warning'}`}>
                        {change.change_type}
                      </span>
                    </td>
                    <td>
                      {change.contract_name ? (
                        <span title={change.contract_name}>{change.contract_name.substring(0, 30)}...</span>
                      ) : (
                        <span style={{ color: '#999' }}>No contract</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Object.keys(change.field_changes).map((field, index) => (
                          <span key={index} className="badge badge-secondary" style={{ fontSize: '10px' }}>
                            {field}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{new Date(change.import_timestamp).toLocaleString()}</td>
                    <td>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => handleReview(change)}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk Review Modal */}
      {showBulkReviewModal && (
        <Modal
          title={`${bulkReviewAction === 'apply' ? 'Apply' : 'Reject'} ${selectedChanges.length} Changes`}
          onClose={() => setShowBulkReviewModal(false)}
        >
          <div>
            <p>
              Are you sure you want to <strong>{bulkReviewAction}</strong> {selectedChanges.length} selected change{selectedChanges.length !== 1 ? 's' : ''}?
            </p>
            
            <div style={{ marginTop: '20px' }}>
              <label>Review Notes (optional):</label>
              <textarea
                value={bulkReviewNotes}
                onChange={(e) => setBulkReviewNotes(e.target.value)}
                className="form-control"
                rows={3}
                placeholder={`Add a note explaining why you ${bulkReviewAction}ed these changes...`}
              />
            </div>
            
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowBulkReviewModal(false)}
              >
                Cancel
              </button>
              <button 
                className={`btn ${bulkReviewAction === 'apply' ? 'btn-success' : 'btn-danger'}`}
                onClick={submitBulkReview}
              >
                {bulkReviewAction === 'apply' ? 'Apply' : 'Reject'} Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedChange && (
        <Modal
          title={`Review ${selectedChange.entity_type} Change`}
          onClose={() => setShowReviewModal(false)}
        >
          <div>
            <h4>{selectedChange.entity_name}</h4>
            <p>Change Type: <strong>{selectedChange.change_type}</strong></p>
            {selectedChange.contract_name && (
              <p>Contract: <strong>{selectedChange.contract_name}</strong></p>
            )}
            
            <div style={{ marginTop: '20px' }}>
              <h5>Field Changes:</h5>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {Object.entries(selectedChange.field_changes).map(([field, values]) => (
                  <div key={field} style={{ 
                    padding: '10px', 
                    marginBottom: '10px', 
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: reviewAction === 'partial' && !selectedFields.includes(field) ? '#f5f5f5' : '#fff'
                  }}>
                    {reviewAction === 'partial' && (
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field)}
                        onChange={() => toggleFieldSelection(field)}
                        style={{ marginRight: '10px' }}
                      />
                    )}
                    <strong>{field}:</strong>
                    <div style={{ marginLeft: reviewAction === 'partial' ? '25px' : '0' }}>
                      <div>Old: <code>{JSON.stringify(values.old_value)}</code></div>
                      <div>New: <code>{JSON.stringify(values.new_value)}</code></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ marginTop: '20px' }}>
              <label>Review Action:</label>
              <select 
                value={reviewAction} 
                onChange={(e) => setReviewAction(e.target.value as any)}
                className="form-control"
              >
                <option value="apply">Apply All Changes</option>
                <option value="partial">Apply Selected Changes</option>
                <option value="reject">Reject Changes</option>
              </select>
            </div>
            
            <div style={{ marginTop: '15px' }}>
              <label>Review Notes:</label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                className="form-control"
                rows={3}
                placeholder="Optional notes about this review..."
              />
            </div>
            
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowReviewModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={submitReview}
              >
                Submit Review
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ImportChangesDashboard;
